/**
 * Application Navigation Hook
 *
 * Type-safe navigation with autocomplete for route parameters.
 * Wraps React Router's useNavigate with typed route builders.
 *
 * @module shared/hooks/useAppNavigation
 */

import { useCallback, useMemo } from 'react';
import {
  useNavigate,
  useLocation,
  useSearchParams,
  type NavigateOptions,
} from 'react-router-dom';
import {
  RouteKey,
  ROUTE_PATHS,
  buildUrl,
  parseRouteParams,
  type RouteTarget,
  type CalendarParams,
  type TodoListParams,
  type AlbumsParams,
  type RoutesWithParams,
  type RouteParamsMap,
} from '../constants/routes';

// ============================================
// Types
// ============================================

/**
 * Return type for useAppNavigation hook
 */
interface UseAppNavigationReturn {
  /**
   * Navigate to a route with type-safe parameters
   * @example navigateTo({ route: RouteKey.DASHBOARD })
   * @example navigateTo({ route: RouteKey.FAMILY_CALENDAR, params: { date: '2025-12-25' } })
   */
  readonly navigateTo: (target: RouteTarget, options?: NavigateOptions) => void;

  /**
   * Navigate to dashboard
   */
  readonly goToDashboard: () => void;

  /**
   * Navigate to calendar with optional parameters
   */
  readonly goToCalendar: (params?: CalendarParams) => void;

  /**
   * Navigate to todo list with optional parameters
   */
  readonly goToTodoList: (params?: TodoListParams) => void;

  /**
   * Navigate to albums with optional parameters
   */
  readonly goToAlbums: (params?: AlbumsParams) => void;

  /**
   * Navigate back in history
   */
  readonly goBack: () => void;

  /**
   * Get the URL for a route (for links, not navigation)
   */
  readonly getUrl: (target: RouteTarget) => string;

  /**
   * Check if the current location matches a route
   */
  readonly isCurrentRoute: (route: RouteKey) => boolean;

  /**
   * Current route's path
   */
  readonly currentPath: string;
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Type-safe navigation hook with autocomplete
 *
 * @example
 * ```typescript
 * const { navigateTo, goToCalendar } = useAppNavigation();
 *
 * // Navigate using RouteTarget
 * navigateTo({ route: RouteKey.DASHBOARD });
 *
 * // Navigate with parameters
 * goToCalendar({ date: '2025-12-25', eventId: 'event-123' });
 *
 * // Or using the generic navigateTo with params
 * navigateTo({
 *   route: RouteKey.FAMILY_CALENDAR,
 *   params: { date: '2025-12-25' }
 * });
 * ```
 */
export function useAppNavigation(): UseAppNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate to a target
   */
  const navigateTo = useCallback(
    (target: RouteTarget, options?: NavigateOptions) => {
      const url = buildUrl(target);
      void navigate(url, options);
    },
    [navigate]
  );

  /**
   * Navigate to dashboard
   */
  const goToDashboard = useCallback(() => {
    void navigate(ROUTE_PATHS[RouteKey.DASHBOARD]);
  }, [navigate]);

  /**
   * Navigate to calendar
   */
  const goToCalendar = useCallback(
    (params?: CalendarParams) => {
      const url = params
        ? buildUrl({ route: RouteKey.FAMILY_CALENDAR, params })
        : buildUrl({ route: RouteKey.FAMILY_CALENDAR });
      void navigate(url);
    },
    [navigate]
  );

  /**
   * Navigate to todo list
   */
  const goToTodoList = useCallback(
    (params?: TodoListParams) => {
      const url = params
        ? buildUrl({ route: RouteKey.TODO_LIST, params })
        : buildUrl({ route: RouteKey.TODO_LIST });
      void navigate(url);
    },
    [navigate]
  );

  /**
   * Navigate to albums
   */
  const goToAlbums = useCallback(
    (params?: AlbumsParams) => {
      const url = params
        ? buildUrl({ route: RouteKey.ALBUMS, params })
        : buildUrl({ route: RouteKey.ALBUMS });
      void navigate(url);
    },
    [navigate]
  );

  /**
   * Navigate back
   */
  const goBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  /**
   * Get URL without navigating
   */
  const getUrl = useCallback((target: RouteTarget): string => {
    return buildUrl(target);
  }, []);

  /**
   * Check if current path matches a route
   */
  const isCurrentRoute = useCallback(
    (route: RouteKey): boolean => {
      const routePath = ROUTE_PATHS[route];
      return (
        location.pathname === routePath ||
        location.pathname.startsWith(`${routePath}/`)
      );
    },
    [location.pathname]
  );

  return {
    navigateTo,
    goToDashboard,
    goToCalendar,
    goToTodoList,
    goToAlbums,
    goBack,
    getUrl,
    isCurrentRoute,
    currentPath: location.pathname,
  };
}

// ============================================
// Route Params Hook
// ============================================

/**
 * Hook to read and parse URL search params for a specific route.
 * Returns typed params with setter functions.
 *
 * @example
 * ```typescript
 * // In FamilyCalendarPage.tsx
 * const { params, setParams, clearParams } = useRouteParams(RouteKey.FAMILY_CALENDAR);
 * // params: { date?: string; eventId?: string }
 *
 * // Update a param
 * setParams({ date: '2025-12-25' });
 *
 * // Clear all params
 * clearParams();
 * ```
 */
export function useRouteParams<K extends RoutesWithParams>(route: K) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Current params parsed from URL
   */
  const params = useMemo(
    () => parseRouteParams(route, searchParams),
    [route, searchParams]
  );

  /**
   * Update params (merges with existing)
   */
  const setParams = useCallback(
    (newParams: Partial<RouteParamsMap[K]>) => {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);

        // Map of param keys to URL keys
        const keyMap: Record<string, string> = {
          date: 'date',
          eventId: 'event',
          taskId: 'task',
          filter: 'filter',
          albumId: 'album',
          photoId: 'photo',
        };

        for (const [key, value] of Object.entries(newParams)) {
          const urlKey = keyMap[key] ?? key;
          if (value === undefined || value === null || value === '') {
            updated.delete(urlKey);
          } else {
            updated.set(
              urlKey,
              typeof value === 'string' ? value : JSON.stringify(value)
            );
          }
        }

        return updated;
      });
    },
    [setSearchParams]
  );

  /**
   * Clear all params
   */
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  /**
   * Remove a specific param
   */
  const removeParam = useCallback(
    (key: keyof RouteParamsMap[K]) => {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);
        const keyMap: Record<string, string> = {
          date: 'date',
          eventId: 'event',
          taskId: 'task',
          filter: 'filter',
          albumId: 'album',
          photoId: 'photo',
        };
        const urlKey = keyMap[key as string] ?? (key as string);
        updated.delete(urlKey);
        return updated;
      });
    },
    [setSearchParams]
  );

  return {
    params,
    setParams,
    clearParams,
    removeParam,
  };
}

// ============================================
// Re-exports
// ============================================

export {
  RouteKey,
  ROUTE_PATHS,
  buildUrl,
  type RouteTarget,
  type CalendarParams,
  type TodoListParams,
  type AlbumsParams,
} from '../constants/routes';
